/**
 * Segment by — the secondary dimension every item report shares (roadmap RP-7).
 *
 * A segment names one item field; a report groups its rows by that field's
 * value and draws one bar per value. The field keys are the item-field
 * registry's keys (config/itemFields.ts), read through the same `getFieldValue`
 * the item list uses, so a report's "by queue" and the list's Queue column
 * agree. Each segment also names the statement alias the drill link uses, so
 * clicking a bar lands on the item list filtered to that value.
 */

import { getFieldValue, type ItemRow } from '../../item-list/hooks/useItemListData';

export type SegmentKey = 'none' | 'category' | 'altitude' | 'queue' | 'orgUnit' | 'owner';

export interface SegmentDefinition {
  key: SegmentKey;
  label: string;
  /** Item-field registry key read through getFieldValue. */
  field: string;
  /** Statement field for the drill link (an alias the parser accepts). */
  pqlField: string;
  /** Copy for the bucket with no value. */
  emptyLabel: string;
}

export const SEGMENTS: ReadonlyArray<SegmentDefinition> = [
  { key: 'category', label: 'Category', field: 'itemCategory', pqlField: 'category', emptyLabel: 'Uncategorized' },
  { key: 'altitude', label: 'Altitude', field: 'altitude', pqlField: 'altitude', emptyLabel: 'No altitude' },
  { key: 'queue', label: 'Queue', field: 'queue', pqlField: 'queue', emptyLabel: 'No queue' },
  { key: 'orgUnit', label: 'Org unit', field: 'orgUnit', pqlField: 'orgUnit', emptyLabel: 'No org unit' },
  { key: 'owner', label: 'Owner', field: 'ownerId', pqlField: 'ownerId', emptyLabel: 'No owner' },
];

export const SEGMENT_OPTIONS: ReadonlyArray<{ value: SegmentKey; label: string }> = [
  { value: 'none', label: 'No segment' },
  ...SEGMENTS.map((s) => ({ value: s.key, label: s.label })),
];

export function segmentFromParam(value: string | null): SegmentKey {
  if (!value) return 'none';
  return SEGMENTS.some((s) => s.key === value) ? (value as SegmentKey) : 'none';
}

export function segmentDefinition(key: SegmentKey): SegmentDefinition | null {
  return SEGMENTS.find((s) => s.key === key) ?? null;
}

/** Names for ids: org units and people. A missing name falls back to the id. */
export interface SegmentNames {
  orgUnits?: ReadonlyMap<string, { name: string; displayName?: string }> | Record<string, string>;
  people?: Record<string, string>;
}

/** The raw segment value of a row: a category, a level, or an id. Null when unset. */
export function segmentValue(row: ItemRow, def: SegmentDefinition): string | null {
  const raw = getFieldValue(row, def.field);
  if (raw === null || raw === undefined || raw === '') return null;
  if (Array.isArray(raw)) return raw.length ? String(raw[0]) : null;
  return String(raw);
}

function nameFrom(map: SegmentNames['orgUnits'], id: string): string | null {
  if (!map) return null;
  if (map instanceof Map) {
    const u = map.get(id);
    return u ? (u.displayName || u.name) : null;
  }
  return (map as Record<string, string>)[id] ?? null;
}

/** problem → Problem; the one copy the report pages share. */
export function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** What the bar is called. */
export function segmentLabel(def: SegmentDefinition, value: string | null, names: SegmentNames = {}): string {
  if (value === null) return def.emptyLabel;
  switch (def.key) {
    case 'category': return titleCase(value);
    case 'altitude': return `Level ${value}`;
    case 'queue':
    case 'orgUnit': return nameFrom(names.orgUnits, value) ?? value;
    case 'owner': return names.people?.[value] ?? value;
    default: return value;
  }
}

export interface SegmentBucket {
  value: string | null;
  label: string;
  rows: ItemRow[];
}

/** Group rows by the segment, largest bucket first; the empty bucket last. */
export function bucketBySegment(rows: ReadonlyArray<ItemRow>, def: SegmentDefinition, names: SegmentNames = {}): SegmentBucket[] {
  const map = new Map<string | null, ItemRow[]>();
  for (const row of rows) {
    const v = segmentValue(row, def);
    const list = map.get(v);
    if (list) list.push(row); else map.set(v, [row]);
  }
  const buckets: SegmentBucket[] = [];
  for (const [value, bucketRows] of map) {
    buckets.push({ value, label: segmentLabel(def, value, names), rows: bucketRows });
  }
  buckets.sort((a, b) => {
    if (a.value === null) return 1;
    if (b.value === null) return -1;
    return b.rows.length - a.rows.length || a.label.localeCompare(b.label);
  });
  return buckets;
}
